import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelL } from '@sorare/core/src/atoms/typography';
import type { SectionType } from '@sorare/core/src/atoms/ui/SectionsBar';
import { SectionsBar } from '@sorare/core/src/atoms/ui/SectionsBar';
import type { RGBColor } from '@sorare/core/src/style/types';

import { ContenderScore } from 'components/lineup/Contender/ContenderFooter/ContenderScore';
import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';
import { messages } from 'components/managerTeam/Status/messages';
import { hasGamesStarted, isFixtureOpened } from 'lib/so5';

import type {
  DivisionChangeConfigBar_so5Leaderboard,
  DivisionChangeConfigBar_so5Lineup,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 1 })`
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  backdrop-filter: blur(32px);

  --c-rgb-promoted: var(--c-rgb-onBase-success);
  --c-rgb-relegated: 255, 78, 34;
  --c-rgb-stable: var(--c-rgb-white);
`;

const HUNDRED_BASIS_POINT = 10000;

type Props = {
  so5Leaderboard: DivisionChangeConfigBar_so5Leaderboard;
  so5Lineup?: Nullable<DivisionChangeConfigBar_so5Lineup>;
};

const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
  align-self: flex-end;
`;

export const DivisionChangeConfigBar = ({
  so5Leaderboard,
  so5Lineup,
}: Props) => {
  const { divisionChangeConfiguration, so5LineupsCount } = so5Leaderboard;
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);
  const isStarted = so5Lineup ? hasGamesStarted(so5Lineup) : !isUpcoming;

  if (!divisionChangeConfiguration) {
    return null;
  }

  const {
    lastPromotedSo5Ranking,
    lastStableSo5Ranking,
    promotionRankBasisPoint,
    relegationRankBasisPoint,
  } = divisionChangeConfiguration;

  const so5Ranking = so5Lineup?.so5Rankings[0];

  const sections: SectionType[] = [
    ...(relegationRankBasisPoint > 0
      ? [
          {
            width: `${relegationRankBasisPoint / 100}%`,
            background:
              so5Ranking?.eligibleDivisionChange?.toLowerCase() === 'relegated'
                ? ('rgba(255, 78, 34, 0.50)' as RGBColor)
                : ('rgba(255, 78, 34, 0.25)' as RGBColor),
            content: !isStarted && (
              <BrandXXS as="p" color="var(--c-nl-600)">
                <FormattedNumber
                  value={relegationRankBasisPoint / HUNDRED_BASIS_POINT}
                  maximumFractionDigits={0}
                  style="percent"
                />
              </BrandXXS>
            ),
          },
        ]
      : []),
    {
      content: (
        <ConditionalWrapper
          Wrapper={StyledSBHorizontal}
          wrap={!!lastStableSo5Ranking || !!lastPromotedSo5Ranking}
        >
          <BrandXXS as="p" color="var(--c-nd-400)">
            {lastStableSo5Ranking ? (
              <FormattedNumber
                value={Math.floor(lastStableSo5Ranking?.score)}
              />
            ) : (
              <FormattedNumber
                value={
                  (HUNDRED_BASIS_POINT -
                    promotionRankBasisPoint -
                    relegationRankBasisPoint) /
                  HUNDRED_BASIS_POINT
                }
                maximumFractionDigits={0}
                style="percent"
              />
            )}
          </BrandXXS>
          {!!lastPromotedSo5Ranking && (
            <BrandXXS as="p" color="var(--c-nd-400)">
              <FormattedNumber
                value={Math.floor(lastPromotedSo5Ranking?.score)}
              />
            </BrandXXS>
          )}
        </ConditionalWrapper>
      ),
    },
    ...(promotionRankBasisPoint > 0
      ? [
          {
            width: `${promotionRankBasisPoint / 100}%`,
            background:
              so5Ranking?.eligibleDivisionChange?.toLowerCase() === 'promoted'
                ? ('rgba(var(--c-rgb-onBase-success), 0.5)' as RGBColor)
                : ('rgba(var(--c-rgb-onBase-success), 0.25)' as RGBColor),
            content: (
              <BrandXXS as="p" color="var(--c-nl-600)">
                {!lastPromotedSo5Ranking && (
                  <FormattedNumber
                    value={promotionRankBasisPoint / HUNDRED_BASIS_POINT}
                    maximumFractionDigits={0}
                    style="percent"
                  />
                )}
              </BrandXXS>
            ),
          },
        ]
      : []),
  ];

  return (
    <Root>
      {!!so5Ranking && (
        <SBHorizontal>
          {!!so5Ranking.eligibleDivisionChange && (
            <Horizontal>
              <DumbStatus divisionChange={so5Ranking.eligibleDivisionChange} />
              <LabelL bold>
                <FormattedMessage
                  {...messages[so5Ranking.eligibleDivisionChange]}
                />
              </LabelL>
            </Horizontal>
          )}
          <ContenderScore score={so5Ranking.score} Typography={LabelL} />
        </SBHorizontal>
      )}
      <SectionsBar
        sections={sections}
        cursorProps={
          !!so5Ranking?.ranking && !!so5Ranking.eligibleDivisionChange
            ? {
                color: `rgba(var(--c-rgb-${so5Ranking.eligibleDivisionChange?.toLowerCase()}), 1)`,
                rightPosition: `${
                  ((so5Ranking.ranking - 1) / (so5LineupsCount - 1)) * 100
                }%`,
              }
            : undefined
        }
      />
    </Root>
  );
};

DivisionChangeConfigBar.fragments = {
  so5Leaderboard: gql`
    fragment DivisionChangeConfigBar_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      so5Fixture {
        slug
        aasmState
      }
      divisionChangeConfiguration {
        id
        lastPromotedSo5Ranking {
          id
          score
        }
        lastStableSo5Ranking {
          id
          score
        }
        promotionRankBasisPoint
        relegationRankBasisPoint
      }
    }
  ` as TypedDocumentNode<DivisionChangeConfigBar_so5Leaderboard>,
  so5Lineup: gql`
    fragment DivisionChangeConfigBar_so5Lineup on So5Lineup {
      id
      so5Rankings {
        id
        ranking
        score
        eligibleDivisionChange
      }
      ...hasGamesStarted_so5Lineup
    }
    ${hasGamesStarted.fragments.so5Lineup}
  ` as TypedDocumentNode<DivisionChangeConfigBar_so5Lineup>,
};
